import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Trans } from '@lingui/macro';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useConceptLabelFormatter } from 'reducers/entityLabelFormatter';
import { conceptsSelector } from 'selectors/entities';

import LightText from 'components/ui/Text';
import DeletableLabel from 'components/ui/button/DeletableLabel';

import commonPropTypes from 'utils/commonPropTypes';

import * as svars from 'assets/style/variables';

const Tree = styled.div`
  & ul {
    padding-inline-start: 0;
    margin: 0;
    padding: 0;
  }
  & ul li {
    list-style-type: none;
    display: flex;
    flex-direction: column;
  }

  & ul li:last-child {
    border-color: transparent;
  }

  & > ul > li:first-child::before {
    content: none;
  }
`;

const buildHierarchy = (classifications, concepts) => {
  const map = {};
  classifications?.forEach((classification) => {
    map[classification.db_concept.id] = { ...classification, children: [] };
  });
  const roots = [];
  classifications?.forEach((classification) => {
    const parentId = concepts[classification.db_concept.id]?.parent?.id;
    if (parentId && map[parentId]) {
      map[parentId].children.push(map[classification.db_concept.id]);
    } else {
      roots.push(map[classification.db_concept.id]);
    }
  });
  return roots;
};
const branchSizePx = 12;

const HorizontalBranch = styled.span`
  display: inline-block;
  border-top: 2px ${svars.fontColorLighter} solid;
  min-width: ${branchSizePx}px;
  max-width: 100%;
  margin: auto 0;
`;

const VerticalBranch = styled.div`
  border-right: 2px solid ${svars.fontColorLighter};

  margin-bottom: ${svars.spaceMedium};
  min-width: ${({ nested }) => `${branchSizePx * (nested + 1)}px`};
`;

const ConceptContainer = styled.span`
  display: flex;
  flex-direction: ${({ row }) => (row ? 'row' : 'column')};
  padding-top: ${svars.spaceNormal};
  width: fit-content;
`;
function ConceptClassifications({
  classifications,
  onDeleteClassification,
  conceptLabelFormatter,
  nested,
  justAddedIds,
}) {
  return (
    <ul>
      {classifications.map((classification) => (
        <li key={classification.db_concept.id}>
          <ConceptContainer
            row={classification.children.length === 0 ? '1' : null}
          >
            <span style={{ display: 'flex', flexDirection: 'row' }}>
              {nested ? <HorizontalBranch /> : null}
              <DeletableLabel
                animate={justAddedIds.includes(classification.db_concept.id)}
                content={conceptLabelFormatter(classification.db_concept.id)}
                onClick={onDeleteClassification(classification.db_concept.id)}
              />
            </span>
            {classification.children.length > 0 && (
              <div style={{ display: 'inline-flex' }}>
                <VerticalBranch nested={nested} />
                <ConceptClassifications
                  classifications={classification.children}
                  onDeleteClassification={onDeleteClassification}
                  conceptLabelFormatter={conceptLabelFormatter}
                  nested={nested ? nested + 1 : 1}
                  justAddedIds={justAddedIds}
                />
              </div>
            )}
          </ConceptContainer>
        </li>
      ))}
    </ul>
  );
}

ConceptClassifications.propTypes = {
  classifications: commonPropTypes.itemsClassifications,
  onDeleteClassification: PropTypes.func.isRequired,
  conceptLabelFormatter: PropTypes.func.isRequired,
  nested: PropTypes.number,
  justAddedIds: PropTypes.arrayOf(PropTypes.number).isRequired,
};

ConceptClassifications.defaultProps = {
  classifications: [],
  nested: 0,
};

function ClassificationValidationForm({
  classifications,
  onDeleteClassification,
  style,
}) {
  const concepts = useSelector(conceptsSelector());
  const conceptLabelFormatter = useConceptLabelFormatter();
  const [justAddedIds, setJustAddedIds] = useState([]);
  const isFirstRender = useRef(true);
  const prevClassificationsRef = useRef([]);

  const nestedClassifications = useMemo(
    () => buildHierarchy(classifications, concepts),
    [classifications, concepts]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevClassificationsRef.current = classifications;
      return null;
    }

    const prevClassifications = prevClassificationsRef.current;
    const newIds = classifications
      .filter(
        (classification) =>
          !prevClassifications.some(
            (prev) => prev.db_concept.id === classification.db_concept.id
          )
      )
      .map((classification) => classification.db_concept.id);

    setJustAddedIds([...justAddedIds, ...newIds]);
    prevClassificationsRef.current = classifications;
    return null;
  }, [classifications]);

  return (
    <Tree style={style}>
      {classifications?.length ? (
        <ConceptClassifications
          classifications={nestedClassifications}
          onDeleteClassification={onDeleteClassification}
          conceptLabelFormatter={conceptLabelFormatter}
          justAddedIds={justAddedIds}
        />
      ) : (
        <LightText style={{ fontStyle: 'italic' }}>
          <Trans id="no-classification-yet" />
        </LightText>
      )}
    </Tree>
  );
}

ClassificationValidationForm.propTypes = {
  classifications: commonPropTypes.chunkClassifications.isRequired,
  style: commonPropTypes.style,
  onDeleteClassification: PropTypes.func.isRequired,
};

ClassificationValidationForm.defaultProps = {
  style: {},
};

export default ClassificationValidationForm;
